import { styled } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { fetchNiches } from "../../../../store/niches/niches-action";
import { fetchTags } from "../../../../store/tags/tags-action";
import HorizontalLabelPositionBelowStepper from "../../Stepper";
import AuthMoveBack from "./AuthMoveBack";

import SignUpBirthStep from "./SignUp/SignUpBirthStep";
import SignUpUsernameStep from "./SignUp/SignUpUsernameStep";
import SignUpPasswordStep from "./SignUp/SignUpPasswordStep";
import SignUpCodeStep from "./SignUp/SignUpCodeStep";
import SignUpPreferencesStep from "./SignUp/SignUpPreferencesStep";
import SignUpTopicsStep from "./SignUp/SignUpTopicsStep";
import SignUpNichesStep from "./SignUp/SignUpNichesStep";
import SignUpIncorrectBirthStep from "./SignUp/SignUpIncorrectBirthStep";
import { AxiosError } from "axios";
import { IErrorData } from "services/types";

import { signup } from "services/Auth";
import { metrics } from "utils/metrics";

interface IProps {
  currentStep: number;
  prev: (decrement?: number) => void;
  next: (increment?: number) => void;
}

export interface ISignUpFieldValues {
  birthday: number;
  username: string;
  email: string;
  password: string;
  password_confirm: string;
  validation_code: string;
  validation_token: string;
  is_confirm_email: boolean;
  preferences: string[];
  niches: string[];
  tags: string[];
  captcha: string;
}

const BIRTH_STEP = 0;
const INCORRECT_BIRTH_STEP = 1;
const USERNAME_STEP = 2;
const CODE_STEP = 3;
const PASSWORD_STEP = 4;
const PREFERENCES_STEP = 5;
const NICHES_STEP = 6;
const TOPICS_STEP = 7;

const STEP_TITLES = [
  "When is your Birthday?",
  "",
  "Email",
  "Confirmation",
  "Password",
  "Define your Preferences",
  "What are you into?",
  "What are you into?"
];

const ADULT_YEAR = 18;
const MS_IN_SEC = 1000;

const AuthSignUp = (props: IProps) => {
  const { currentStep, prev, next } = props;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);

  const { handleSubmit, setValue, getValues, watch, control, trigger, register } = useForm<ISignUpFieldValues>();

  const dispatch = useDispatch();

  const sendSignUpEventView = async () => {
    await metrics.sendSignUpFormView();
  };

  const isValidBirth = () => {
    const value = getValues().birthday;
    const currentDate = new Date();
    const acceptableDate = new Date(currentDate.getFullYear() - ADULT_YEAR, currentDate.getMonth(), currentDate.getDate());
    return value * MS_IN_SEC <= acceptableDate.getTime();
  };

  const onSubmit = async (data: ISignUpFieldValues) => {
    setErrorMessage(null);
    setDisabled(true);

    const {
      username,
      email,
      password,
      birthday,
      validation_code,
      validation_token,
      preferences,
      niches,
      tags
    } = data;

    const SECONDS_IN_MINUTE = 60;

    try {
      await signup({
        username,
        email,
        password,
        birthday: birthday + new Date().getTimezoneOffset() * SECONDS_IN_MINUTE,
        validation_code,
        validation_token,
        preferences: preferences.join(","),
        niches: niches.join(","),
        tags: tags.join(",")
      });
      await metrics.sendSignUpSuccessful();
    } catch (e) {
      console.warn(e);
      const message = (e as AxiosError<IErrorData>).response?.data?.error?.message || "Service unavailable";
      setErrorMessage(message);
    } finally {
      setDisabled(false);
    }
  };

  const handleNext = () => {
    if (currentStep === BIRTH_STEP && isValidBirth()) {
      next(2);
      return;
    }

    if (currentStep === USERNAME_STEP && getValues().is_confirm_email) {
      next(2);
      return;
    }

    next();
  };

  const handleBack = () => {
    if (currentStep === PASSWORD_STEP && getValues().is_confirm_email) {
      prev(2);
      return;
    }

    if (currentStep === USERNAME_STEP) {
      prev(2);
      return;
    }

    prev();
  };

  useEffect(() => {
    dispatch(fetchTags());
    dispatch(fetchNiches());
  }, [dispatch]);

  useEffect(() => {
    sendSignUpEventView();
  }, []);

  return (
    <Fragment>
      {
        currentStep !== INCORRECT_BIRTH_STEP && (
          <Fragment>
            <HorizontalLabelPositionBelowStepper currentStep={currentStep} stepsCount={7} isAuthProgress />
            <MarginContainer>
              <AuthMoveBack back={currentStep !== 0 ? handleBack : undefined} text={STEP_TITLES[currentStep]}/>
            </MarginContainer>
          </Fragment>
        )
      }

      <form onSubmit={handleSubmit(onSubmit)}>
        {currentStep === BIRTH_STEP && (
          <SignUpBirthStep next={handleNext} setValue={setValue}/>
        )}

        {currentStep === INCORRECT_BIRTH_STEP && (
          <SignUpIncorrectBirthStep back={handleBack}/>
        )}

        {currentStep === USERNAME_STEP && (
          <SignUpUsernameStep
            next={handleNext}
            control={control}
            trigger={trigger}
            setValue={setValue}
            getValues={getValues}
            register={register}
          />
        )}

        {currentStep === CODE_STEP && (
          <SignUpCodeStep
            next={handleNext}
            back={handleBack}
            control={control}
            trigger={trigger}
            setValue={setValue}
            getValues={getValues}
          />
        )}

        {currentStep === PASSWORD_STEP && (
          <SignUpPasswordStep
            next={handleNext}
            control={control}
            trigger={trigger}
            watch={watch}
            getValues={getValues}
          />
        )}

        {currentStep === PREFERENCES_STEP && (
          <SignUpPreferencesStep
            next={handleNext}
            setValue={setValue}
            getValues={getValues}
          />
        )}

        {currentStep === NICHES_STEP && (
          <SignUpNichesStep
            setValue={setValue}
            getValues={getValues}
            disabled={disabled}
            next={next}
          />
        )}

        {currentStep === TOPICS_STEP && (
          <SignUpTopicsStep
            setValue={setValue}
            getValues={getValues}
            disabled={disabled}
          />
        )}

        { errorMessage &&
          <ErrorMessage>
            { errorMessage }
          </ErrorMessage>
        }
      </form>
    </Fragment>
  );
};

export default AuthSignUp;

const MarginContainer = styled("div")({
  marginTop: "32px"
});

const ErrorMessage = styled("div")({
  marginTop: "1rem",
  color: "red",
  textAlign: "center",
  fontWeight: 400,
  fontSize: "0.75rem",
});
